import { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface RedirectRouteProps {
  to: string;
  /** If true, preserves params and query string */
  preserveQuery?: boolean;
}

/**
 * Client-side 301 redirect with hit logging to seo_redirects table.
 * Use for legacy paths that should funnel SEO juice to the new canonical URL.
 */
export const RedirectRoute = ({ to, preserveQuery = true }: RedirectRouteProps) => {
  const location = useLocation();
  const params = useParams();

  // Resolve dynamic segments (e.g. :slug) in the target URL
  let resolvedTo = to;
  Object.entries(params).forEach(([key, value]) => {
    if (value) resolvedTo = resolvedTo.replace(`:${key}`, value);
  });

  const target = preserveQuery ? `${resolvedTo}${location.search}${location.hash}` : resolvedTo;

  useEffect(() => {
    // Fire-and-forget hit count update — don't block navigation
    const fromPath = location.pathname;
    void supabase
      .from('seo_redirects')
      .select('id, hit_count')
      .eq('from_path', fromPath)
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        void supabase
          .from('seo_redirects')
          .update({ hit_count: (data.hit_count ?? 0) + 1, last_hit_at: new Date().toISOString() })
          .eq('id', data.id);
      });
  }, [location.pathname]);

  return <Navigate to={target} replace />;
};
